import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronDown } from 'lucide-react';
import { companyInfo } from '../data';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [timeoutId, setTimeoutId] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Fetch services dynamically to keep the dropdown updated
    fetch('https://api.dev.auxo.dk/public/content/ydelser')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch((err) => {
        console.error('Error fetching services for navbar:', err);
        // Fallback in case of error
        setServices([
          { title: 'Facade- og murværk', service_slug: 'facade-og-murvaerk' },
          { title: 'Nyt murværk & Tilbygninger', service_slug: 'nyt-murvaerk-og-tilbygninger' },
          { title: 'Omfugning & Reparation', service_slug: 'omfugning-og-reparation' }
        ]);
      });
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const handleMouseEnter = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    const id = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 250); // 250ms delay to make navigation smoother
    setTimeoutId(id);
  };

  const navItems = [
    { name: 'Forside', path: '/' },
    { name: 'Ydelser', path: '/ydelser', hasDropdown: true },
    { name: 'Profil', path: '/profil' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="relative z-50 w-full shadow-sm">
      {/* Top Contact Bar */}
      <div className="hidden md:block bg-neutral-950 text-neutral-300 text-xs py-2.5 px-4 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-4">
            <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="flex items-center gap-1.5 hover:text-red-500 transition-colors">
              <Phone size={13} className="text-white" />
              <span>{companyInfo.phone}</span>
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-1.5 hover:text-red-500 transition-colors">
              <Mail size={13} className="text-white" />
              <span>{companyInfo.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-1.5 text-neutral-300">
            <MapPin size={13} className="text-white" />
            <span>{companyInfo.address}</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white text-neutral-900 py-4 px-4 md:px-6 border-b border-neutral-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" onClick={() => setIsOpen(false)}>
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-extrabold tracking-tight text-neutral-950 flex items-center gap-1.5">
                <span className="text-red-700">PEDERSEN</span>
                <span className="font-light text-neutral-700">BYG</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-semibold -mt-1">
                v/ Christian Juhl Pedersen
              </span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <div className="flex items-center gap-6">
              {navItems.map((item) => {
                if (item.hasDropdown) {
                  return (
                    <div
                      key={item.name}
                      className="relative py-2"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        className={`text-sm font-extrabold tracking-wide uppercase transition-colors flex items-center gap-1 cursor-pointer ${
                          isActive(item.path)
                            ? 'text-red-700'
                            : 'text-neutral-600 hover:text-neutral-950'
                        }`}
                      >
                        <span>{item.name}</span>
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${
                            isDropdownOpen ? 'rotate-180 text-red-700' : 'text-neutral-400'
                          }`}
                        />
                      </button>

                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div 
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white border border-neutral-100 rounded-sm shadow-xl py-2 animate-in fade-in slide-in-from-top-2 duration-150 z-50"
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        >
                          {services.map((service) => (
                            <Link
                              key={service.id || service.service_slug}
                              to={`/ydelser/${service.service_slug}`}
                              onClick={() => setIsDropdownOpen(false)}
                              className="block px-5 py-3 text-sm font-medium text-neutral-700 hover:text-neutral-950 hover:bg-neutral-50 transition-colors"
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`text-sm font-extrabold tracking-wide uppercase transition-colors py-2 border-b-2 ${
                      isActive(item.path)
                        ? 'border-red-700 text-red-700'
                        : 'border-transparent text-neutral-600 hover:text-neutral-950 hover:border-neutral-300'
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
            <Link
              to="/kontakt"
              className="bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md shadow-sm"
            >
              Få et tilbud
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-neutral-700 hover:text-neutral-950 focus:outline-none"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {isOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-neutral-200 shadow-lg py-4 px-6 flex flex-col gap-2 animate-in fade-in slide-in-from-top-5 duration-200 z-50 max-h-[calc(100vh-120px)] overflow-y-auto">
            {navItems.map((item) => {
              if (item.hasDropdown) {
                return (
                  <div key={item.name} className="flex flex-col">
                    <button
                      onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                      className={`text-sm font-extrabold tracking-wide uppercase py-2 border-b border-neutral-100 flex justify-between items-center w-full text-left cursor-pointer ${
                        isActive(item.path) ? 'text-red-700' : 'text-neutral-600'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${
                          isMobileDropdownOpen ? 'rotate-180 text-red-700' : 'text-neutral-400'
                        }`}
                      />
                    </button>

                    {/* Mobile Dropdown Sub-items */}
                    {isMobileDropdownOpen && (
                      <div className="pl-4 bg-neutral-50/50 rounded-sm flex flex-col border-l-2 border-red-700/30 mt-1 mb-2">
                        {services.map((service) => (
                          <Link
                            key={service.id || service.service_slug}
                            to={`/ydelser/${service.service_slug}`}
                            onClick={() => {
                              setIsOpen(false);
                              setIsMobileDropdownOpen(false);
                            }}
                            className="text-sm font-medium text-neutral-700 py-2.5 border-b border-neutral-100/50 last:border-0"
                          >
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-sm font-extrabold tracking-wide uppercase py-2 border-b border-neutral-100 ${
                    isActive(item.path) ? 'text-red-700' : 'text-neutral-600'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="bg-red-700 hover:bg-red-800 text-white text-center text-xs font-bold uppercase tracking-wider py-3 rounded-sm transition-all duration-200 hover:-translate-y-0.5 mt-2 shadow-sm hover:shadow-md"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
}
